import { Alert, Box, Button, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

/**
 * Shown when an invitation link can't be used. The server answers a used, expired and never-issued
 * code identically on purpose (InvitationLookupDisclosureTests), so this page cannot say which —
 * it covers both likely cases and gives each a way forward.
 *
 * The case that matters most: invitation links are single-use, and they are used up the moment the
 * account is created. A patient who stopped part-way through registration and came back to the
 * email link used to read only "This invite has either expired or is invalid" — and reasonably
 * concluded they were locked out, when signing in with the password they had chosen would have
 * taken them straight back into registration (reported 2026-10-03).
 */
export default function InvitationUnavailable({ canRequestNew = false }: { canRequestNew?: boolean }) {
  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
        This invitation link can&apos;t be used
      </Typography>
      <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
        Invitation links work only once, and expire after 7 days.
      </Alert>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Already created your account?
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Sign in with the email and password you chose. If you didn&apos;t finish setting up your
        account, you&apos;ll be taken back to finish it.
      </Typography>
      <Button component={RouterLink} to="/login" variant="contained" size="large" sx={{ mb: 1.5 }}>
        Sign in
      </Button>
      <Typography variant="body2" sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/forgot-password">
          Forgot your password?
        </Link>
      </Typography>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Haven&apos;t created an account yet?
      </Typography>
      <Typography variant="body1">
        {canRequestNew ? (
          <>
            <Link component={RouterLink} to="/i/resend-invitation">
              Request a new invitation
            </Link>
            , or contact your care team.
          </>
        ) : (
          "Ask whoever invited you to send a new invitation."
        )}
      </Typography>
    </Box>
  );
}
